/**
 * @file CalendarPanel.jsx
 * @description Enhanced calendar view with order counts per day
 */

import React from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isToday
} from 'date-fns';

const CalendarPanel = ({ ordersByDay }) => {
  const ordersMap = {};
  let maxOrders = 0;

  ordersByDay.forEach((d) => {
    if (d.date) {
      const normalized = d.date.slice(0, 10); 
      const count = d.total_orders || 0;
      ordersMap[normalized] = count;
      if (count > maxOrders) maxOrders = count;
    }
  });

  const getBgColor = (count) => {
    if (count === 0) return '';
    if (count < maxOrders * 0.33) return 'bg-blue-200';
    if (count < maxOrders * 0.66) return 'bg-blue-400';
    return 'bg-blue-600';
  };

  const today = new Date();
  const start = startOfMonth(today);
  const end = endOfMonth(today);
  const daysInMonth = eachDayOfInterval({ start, end });

  const renderDayCell = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const count = ordersMap[dateStr] || 0;
    const isNow = isToday(day);
    const bgColor = getBgColor(count);

    return (
      <div key={dateStr} className="flex flex-col items-center justify-center w-12 h-12">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition
            ${count > 0 ? 'text-white' : 'text-gray-400'}
            ${isNow ? 'ring-2 ring-blue-700' : ''}
            ${bgColor}
          `}
          title={`${count} order${count !== 1 ? 's' : ''}`}
        >
          {format(day, 'd')}
        </div>
      </div>
    );
  };

  const renderEmptyCellsBeforeFirst = () => {
    const firstDay = getDay(start);
    return Array.from({ length: firstDay }).map((_, i) => (
      <div key={`empty-${i}`} className="w-12 h-12" />
    ));
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-gray-800">
          Orders {format(today, 'MMMM yyyy')}
        </h2>
        <span className="text-xs text-gray-400">Today marked with ring</span>
      </div>

      <div className="grid grid-cols-7 gap-2 text-center">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
          <div key={d} className="text-xs font-bold text-gray-500">{d}</div>
        ))}
        {renderEmptyCellsBeforeFirst()}
        {daysInMonth.map(renderDayCell)}
      </div>
    </div>
  );
};

export default CalendarPanel;
